import { StrictMode, Component, type ReactNode, type ErrorInfo } from 'react';
import { createRoot } from 'react-dom/client';
import { BrandConfigProvider, type ClientBrandConfig } from './context/BrandConfigContext';
import { ProductCacheProvider } from './ui-components';
import { UserProvider } from './context/UserContext';
import { ChatDemoAnimation } from './components/ChatDemoAnimation';
import './ui-components/styles/design-tokens.css';
import './components/MessageRenderer.css';

const LOCAL_FALLBACK_API = 'https://closera-liveboard-staging-571836375014.us-central1.run.app';
const apiParams = new URLSearchParams(window.location.search);
const apiOverride = apiParams.get('apiBaseUrl') || apiParams.get('api');
const isLocalPreview = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE_URL = apiOverride || import.meta.env.VITE_API_URL || (isLocalPreview ? LOCAL_FALLBACK_API : '');

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('Demo error:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, color: 'red', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          <h2>Demo Error</h2>
          <p>{this.state.error.message}</p>
          <p>{this.state.error.stack}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const JCREW_BRAND_CONFIG: ClientBrandConfig = {
  brandName: 'J.Crew',
  displayName: 'Closera AI',
  iconText: 'C',
  tagline: '',
  defaultPlaceholder: "Ask J.Crew's AI Stylist anything...",
  siteNavButtonText: 'Shop with J.Crew AI',
  colors: {
    accent: '#292929',
    accentHover: '#444444',
    accentLight: '#f5f3f0',
    avatarBackground: '#f5f3f0',
    avatarText: '#292929',
  },
  fonts: {
    brandFont: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    brandFontSize: '16px',
    brandFontWeight: 700,
    brandFontStyle: 'normal',
  },
  iconSvgPaths: [],
  iconViewBox: '0 0 24 24',
  suggestions: [],
  homeChips: [],
  collectionSuggestions: {},
  currency: 'USD',
  locale: 'en-US',
  features: { showSizeHelp: true, showBodyShapeSelector: true, showSizeChart: true, showSizeRecommendation: true, photoAction: 'virtualTryOn' },
  labels: { colorTerm: 'Color', materialTerm: 'Material', sizeTerm: 'Size', bundleTerm: 'Outfit' },
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <UserProvider apiBaseUrl={API_BASE_URL}>
        <BrandConfigProvider config={JCREW_BRAND_CONFIG}>
          <ProductCacheProvider>
            <ChatDemoAnimation apiBaseUrl={API_BASE_URL} />
          </ProductCacheProvider>
        </BrandConfigProvider>
      </UserProvider>
    </ErrorBoundary>
  </StrictMode>
);
